import { supabase, handleSupabaseError } from '../lib/supabase';

export type ValidityBasis = 'booking_time' | 'rental_start';

export interface DiscountCode {
  id: string;
  merchant_id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  valid_from: string | null;
  valid_to: string | null;
  validity_basis: ValidityBasis | null;
  valid_time_from: string | null;
  valid_time_until: string | null;
  valid_days_of_week: number[] | null;
  max_uses: number | null;
  current_uses: number | null;
  max_value: number | null;
  minimum_order_amount: number | null;
  applicable_product_types: string[] | null;
  active: boolean | null;
  current_value_used: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DiscountCodeInput {
  merchant_id: string;
  code: string;
  description?: string | null;
  discount_type: 'percentage' | 'fixed_amount' | 'timecard';
  discount_value: number;
  valid_from?: string | null;
  valid_to?: string | null;
  validity_basis?: ValidityBasis;
  /** Recurring daily window, wall-clock 'HH:MM' in the merchant's timezone,
   * inclusive on both ends; from > until = overnight window. */
  valid_time_from?: string | null;
  valid_time_until?: string | null;
  /** ISO 8601 weekdays, 1 = Monday … 7 = Sunday (differs from
   * pricing_rules.valid_days_of_week). Null/empty = every day. */
  valid_days_of_week?: number[] | null;
  max_uses?: number | null;
  max_value?: number;
  minimum_order_amount?: number | null;
  applicable_product_types?: string[] | null;
  active?: boolean;
}

/**
 * Rejection reasons returned by the shared check_discount_temporal_validity
 * Postgres function (booking-engine migration 139). Stable API — the same
 * codes are used by the customer-facing booking engine.
 */
export type TemporalRejectionReason =
  | 'not_found'
  | 'missing_rental_start'
  | 'not_yet_valid'
  | 'expired'
  | 'invalid_day'
  | 'outside_time_window';

interface GetDiscountCodesParams {
  merchantId?: string | null;
  locationId?: string | null;
  isPlatformAdmin?: boolean;
  active?: boolean;
  discountType?: 'percentage' | 'fixed_amount' | 'timecard';
  search?: string;
}

export async function getDiscountCodes(params: GetDiscountCodesParams) {
  try {
    let query = supabase
      .from('discount_codes')
      .select('*')
      .order('created_at', { ascending: false });

    // Apply merchant filtering
    if (!params.isPlatformAdmin && params.merchantId) {
      query = query.eq('merchant_id', params.merchantId);
    }

    // Filter by active status
    if (params.active !== undefined) {
      query = query.eq('active', params.active);
    }

    // Filter by discount type
    if (params.discountType) {
      query = query.eq('discount_type', params.discountType);
    }

    // Search by code or description
    if (params.search) {
      query = query.or(`code.ilike.%${params.search}%,description.ilike.%${params.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data as DiscountCode[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function getDiscountCodeById(id: string) {
  try {
    const { data, error } = await supabase
      .from('discount_codes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return { data: data as DiscountCode, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Checks if a discount code already exists for a given merchant.
 * Returns true if a duplicate exists, false otherwise.
 * Optionally excludes a specific discount code ID (for updates).
 */
async function isDuplicateCode(merchantId: string, code: string, excludeId?: string): Promise<boolean> {
  let query = supabase
    .from('discount_codes')
    .select('id')
    .eq('merchant_id', merchantId)
    .eq('code', code.toUpperCase());

  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  const { data } = await query.limit(1);
  return (data?.length ?? 0) > 0;
}

export async function createDiscountCode(input: DiscountCodeInput) {
  try {
    // Check for duplicate code within the same merchant
    const duplicate = await isDuplicateCode(input.merchant_id, input.code);
    if (duplicate) {
      return { data: null, error: `A discount code "${input.code.toUpperCase()}" already exists for this merchant` };
    }

    const { data, error } = await supabase
      .from('discount_codes')
      .insert({
        merchant_id: input.merchant_id,
        code: input.code.toUpperCase(), // Normalize to uppercase
        description: input.description || null,
        discount_type: input.discount_type,
        discount_value: input.discount_value,
        valid_from: input.valid_from || null,
        valid_to: input.valid_to || null,
        validity_basis: input.validity_basis ?? 'booking_time',
        valid_time_from: input.valid_time_from || null,
        valid_time_until: input.valid_time_until || null,
        valid_days_of_week: input.valid_days_of_week?.length ? input.valid_days_of_week : null,
        max_uses: input.max_uses || null,
        max_value: input.max_value ?? null,
        minimum_order_amount: input.minimum_order_amount || null,
        applicable_product_types: input.applicable_product_types || null,
        active: input.active ?? true
      })
      .select()
      .single();

    if (error) throw error;

    return { data: data as DiscountCode, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function updateDiscountCode(id: string, updates: Partial<DiscountCodeInput>) {
  try {
    const updateData: any = { ...updates };

    // Normalize code to uppercase if provided
    if (updateData.code) {
      updateData.code = updateData.code.toUpperCase();
    }

    // Check for duplicate code within the same merchant when code is being changed
    if (updateData.code && updateData.merchant_id) {
      const duplicate = await isDuplicateCode(updateData.merchant_id, updateData.code, id);
      if (duplicate) {
        return { data: null, error: `A discount code "${updateData.code}" already exists for this merchant` };
      }
    }

    const { data, error } = await supabase
      .from('discount_codes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: data as DiscountCode, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function deleteDiscountCode(id: string) {
  try {
    const { error } = await supabase
      .from('discount_codes')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/** English fallback messages for the shared temporal rejection reasons. */
const TEMPORAL_REJECTION_MESSAGES: Record<TemporalRejectionReason, string> = {
  not_found: 'Invalid discount code',
  missing_rental_start: 'Please select a rental start date first',
  not_yet_valid: 'This discount code is not yet valid',
  expired: 'This discount code has expired',
  invalid_day: 'This discount code is not valid on the selected day',
  outside_time_window: 'This discount code is not valid at the selected time'
};

/**
 * Validates a discount code for use with a booking
 * Returns the calculated discount amount if valid
 *
 * Temporal validity (date range, validity_basis, daily time window, weekdays)
 * is delegated to the shared check_discount_temporal_validity Postgres
 * function so the admin panel and the customer-facing booking engine can
 * never disagree. `rentalStart` is the booking's start instant (UTC) and is
 * required for codes with validity_basis = 'rental_start'.
 */
export async function validateDiscountCode(params: {
  code: string;
  merchantId: string;
  orderAmount: number;
  productTypeIds?: string[];
  rentalStart?: Date | null;
}) {
  try {
    const { data: discountCode, error } = await supabase
      .from('discount_codes')
      .select('*')
      .eq('code', params.code.toUpperCase())
      .eq('merchant_id', params.merchantId)
      .single();

    if (error) {
      return { valid: false, discount: 0, error: 'Invalid discount code', reason: 'not_found' as TemporalRejectionReason };
    }

    // Check if active
    if (!discountCode.active) {
      return { valid: false, discount: 0, error: 'This discount code is inactive' };
    }

    // Check temporal validity via the shared DB function (single source of
    // truth for boundaries, timezone math and "now")
    const rentalStartIso =
      params.rentalStart && !isNaN(params.rentalStart.getTime())
        ? params.rentalStart.toISOString()
        : null;

    const { data: temporalData, error: temporalError } = await supabase.rpc('check_discount_temporal_validity', {
      p_discount_id: discountCode.id,
      p_rental_start: rentalStartIso,
      p_merchant_id: params.merchantId
    });

    if (temporalError) throw temporalError;

    const temporal = Array.isArray(temporalData) ? temporalData[0] : temporalData;
    if (!temporal?.valid) {
      const reason = (temporal?.reason ?? 'not_found') as TemporalRejectionReason;
      return {
        valid: false,
        discount: 0,
        error: TEMPORAL_REJECTION_MESSAGES[reason] ?? 'Invalid discount code',
        reason
      };
    }

    // Check max uses
    if (discountCode.max_uses && (discountCode.current_uses ?? 0) >= discountCode.max_uses) {
      return { valid: false, discount: 0, error: 'This discount code has reached its usage limit' };
    }

    // Check minimum order amount
    if (discountCode.minimum_order_amount && params.orderAmount < discountCode.minimum_order_amount) {
      return {
        valid: false,
        discount: 0,
        error: `Minimum order amount is ${discountCode.minimum_order_amount}`
      };
    }

    // Check applicable products
    if (discountCode.applicable_product_types && discountCode.applicable_product_types.length > 0 && params.productTypeIds) {
      const hasApplicableProduct = params.productTypeIds.some(
        id => discountCode.applicable_product_types!.includes(id)
      );
      if (!hasApplicableProduct) {
        return { valid: false, discount: 0, error: 'This discount code is not applicable to your selected products' };
      }
    }

    // Calculate discount
    let discountAmount = 0;

    if (discountCode.discount_type === 'percentage') {
      discountAmount = (params.orderAmount * discountCode.discount_value) / 100;
      // Apply max_value cap for percentage discounts
      if (discountCode.max_value != null && discountAmount > discountCode.max_value) {
        discountAmount = discountCode.max_value;
      }
    } else if (discountCode.discount_type === 'fixed_amount') {
      discountAmount = Math.min(discountCode.discount_value, params.orderAmount);
    } else if (discountCode.discount_type === 'timecard') {
      // For timecard, check remaining balance
      const remainingBalance = discountCode.discount_value - (discountCode.current_value_used ?? 0);
      if (remainingBalance <= 0) {
        return { valid: false, discount: 0, error: 'This timecard has been fully used' };
      }
      // Check max value per booking for timecard
      if (discountCode.max_value != null && params.orderAmount > discountCode.max_value) {
        return {
          valid: false,
          discount: 0,
          error: `Maximum order value for this timecard is ${discountCode.max_value}`
        };
      }
      // Discount is the lesser of: remaining balance, order amount, or max_value
      discountAmount = Math.min(remainingBalance, params.orderAmount, discountCode.max_value ?? Infinity);
    }

    return {
      valid: true,
      discount: discountAmount,
      error: null,
      discountCodeId: discountCode.id,
      discountType: discountCode.discount_type
    };
  } catch (error) {
    return { valid: false, discount: 0, error: handleSupabaseError(error) };
  }
}

/**
 * Increments the usage count and value for a discount code
 * Should be called when a booking with a discount code is confirmed
 */
export async function incrementDiscountCodeUsage(discountCodeId: string, usedAmount: number) {
  try {
    // Get current values
    const { data: currentCode, error: fetchError } = await supabase
      .from('discount_codes')
      .select('current_uses, current_value_used')
      .eq('id', discountCodeId)
      .single();

    if (fetchError) throw fetchError;

    // Update with incremented values
    const { error } = await supabase
      .from('discount_codes')
      .update({
        current_uses: (currentCode.current_uses || 0) + 1,
        current_value_used: (currentCode.current_value_used || 0) + usedAmount
      })
      .eq('id', discountCodeId);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
